import { type BarSeriesOption } from 'echarts';
import { util } from 'echarts/core';
import { type GridOption } from 'echarts/types/dist/shared';
import { type ThemeParser } from '../../../core/theme';
import { overrideIfUndefined } from '../../../util/data';
import type StandardChart from '../../../core/StandardChart';
import { type Option } from '../../../core/option';
import { isObject } from '../../../util/utils';
import { getBarColor } from '../static/color';
import { FONT_SIZE_LIST, FONT_WEIGHT_LIST, THS_FONTFAMILY_MEDIUM } from '../static/font';
import { GridThemeParse } from '../component/grid';
import { xAxisPointerThemeParse } from '../component/xAxisPointer';
import { yAxisThemeParse } from '../component/yAxis';
import { xAxisThemeParse } from '../component/xAxis';
import { ToolTipThemeParse } from '../component/toolTip';
import { legendThemeParse } from '../component/legend';
import { judgeIsDynamicBar } from '../../pc/series/dynamicBar';

/** 规范 */
const SPECIFICATION = {
  /** 不同系列柱子间距和柱宽比例为 2:1 */
  barGap: '50%',
  /** 单柱情况下，相邻两组间距和柱宽比例为 2:1 */
  barCategoryGap: '33%',
  /**
   * 最大柱宽
   *
   * ! 用 barMaxWidth 而不用 barWidth 是因为数据密集时需要自适应缩放
   */
  barMaxWidth: 16,
  /** 最小柱高 */
  barMinHeight: {
    /** 单柱 */
    single: 0.5,
    /** 堆叠 */
    stack: 0
  },
  /** 动画 */
  animation: [
    {
      /** 匹配条件 */
      condition: {
        /** 图最小高度 */
        minH: 0,
        /** 图最大高度 */
        maxH: 80
      },
      /** 动画时长 ms */
      time: 320,
      /** 缓动效果 */
      easing: 'cubicOut'
    },
    {
      /** 匹配条件 */
      condition: {
        /** 图最小高度 */
        minH: 80,
        /** 图最大高度 */
        maxH: 160
      },
      /** 动画时长 ms */
      time: 480,
      /** 缓动效果 */
      easing: 'cubicOut'
    }
  ],
  /** 安全边距 */
  safeMargin: 0
};

/**
 * 计算grid的宽高
 * @param dom
 * @param gridOption_
 * @returns
 */
function getGridBoundingRect(dom: HTMLElement, gridOption_: GridOption | Array<GridOption>) {
  const HUNDRED = 100;
  const gridOption = Array.isArray(gridOption_) ? gridOption_ : [gridOption_];
  // echarts的默认值
  const defaultGridOption = {
    top: 60,
    left: '10%',
    right: '10%',
    bottom: 70
  };
  const gridOptionResult = Object.assign(defaultGridOption, gridOption[0]);
  const domWidth = dom.clientWidth;
  const domHeight = dom.clientHeight;
  const topPadding =
    typeof gridOptionResult.top === 'number'
      ? gridOptionResult.top
      : (domHeight * parseFloat(gridOptionResult.top)) / HUNDRED;
  const leftPadding =
    typeof gridOptionResult.left === 'number'
      ? gridOptionResult.left
      : (domWidth * parseFloat(gridOptionResult.left)) / HUNDRED;
  const rightPadding =
    typeof gridOptionResult.right === 'number'
      ? gridOptionResult.right
      : (domWidth * parseFloat(gridOptionResult.right)) / HUNDRED;
  const bottomPadding =
    typeof gridOptionResult.bottom === 'number'
      ? gridOptionResult.bottom
      : (domHeight * parseFloat(gridOptionResult.bottom)) / HUNDRED;

  return {
    x: leftPadding,
    y: topPadding,
    width: domWidth - leftPadding - rightPadding,
    height: domHeight - topPadding - bottomPadding
  };
}

/**
 * 柱间距
 */
export function parseBarGap(seriesOption: BarSeriesOption[]) {
  const groupSize = { single: 0, stack: new Set() };

  seriesOption.forEach(option => {
    // * 不同系列柱子间距和柱宽比例为 2:1
    // eslint-disable-next-line no-param-reassign
    option.barGap = overrideIfUndefined(option.barGap, SPECIFICATION.barGap);

    if (typeof option.stack !== 'undefined') {
      groupSize.stack.add(option.stack);
    } else {
      groupSize.single += 1;
    }
  });

  const groupSizeTotal = groupSize.single + groupSize.stack.size;

  if (groupSizeTotal <= 1) {
    seriesOption.forEach(option => {
      // * 单柱情况下，相邻两组间距和柱宽比例为 2:1
      // eslint-disable-next-line no-param-reassign
      option.barCategoryGap = overrideIfUndefined(
        option.barCategoryGap,
        SPECIFICATION.barCategoryGap
      );
    });
  } else {
    const barCategoryGap = 2 / ((groupSizeTotal + 1) * 1.5);
    seriesOption.forEach(option => {
      // * 多柱情况下，相邻两组柱间距空1柱宽
      // eslint-disable-next-line no-param-reassign
      option.barCategoryGap = overrideIfUndefined(
        option.barCategoryGap,
        `${barCategoryGap * 100}%`
      );
    });
  }
}

/**
 * 柱的尺寸
 */
export function parseBarSize(seriesOption: BarSeriesOption[]) {
  seriesOption.forEach(option => {
    // * 柱宽
    // eslint-disable-next-line no-param-reassign
    option.barMaxWidth = overrideIfUndefined(option.barMaxWidth, SPECIFICATION.barMaxWidth);

    // * 柱高
    // 数据为 0 时的缺失状态
    if (typeof option.stack !== 'undefined') {
      // ! 堆叠的柱子限制柱子的最小高度 2px
      // eslint-disable-next-line no-param-reassign
      option.barMinHeight = overrideIfUndefined(
        option.barMinHeight,
        SPECIFICATION.barMinHeight.stack
      );
    } else {
      // eslint-disable-next-line no-param-reassign
      option.barMinHeight = overrideIfUndefined(
        option.barMinHeight,
        SPECIFICATION.barMinHeight.single
      );
    }
  });
}

/**
 * 柱的标签
 */
function parseBarLabel(seriesOption: BarSeriesOption[]) {
  seriesOption.forEach(option => {
    // ! 堆叠的柱子不处理标签
    if (typeof option.stack !== 'undefined') {
      return;
    }

    if (typeof option.label === 'undefined') {
      // eslint-disable-next-line no-param-reassign
      option.label = {};
    }

    if (typeof option.label.position === 'undefined') {
      // * 标签放置在正负值方向的位置上
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      option.label.dvPosition = 'valueDirection';
    }

    // * 标签文本颜色继承自系列色
    // eslint-disable-next-line no-param-reassign
    option.label.color = overrideIfUndefined(option.label.color, 'inherit');

    // eslint-disable-next-line no-param-reassign
    option.label.fontWeight = overrideIfUndefined(
      option.label.fontWeight,
      FONT_WEIGHT_LIST.barLabel
    );

    // eslint-disable-next-line no-param-reassign
    option.label.fontSize = overrideIfUndefined(option.label.fontSize, FONT_SIZE_LIST.seriesLabel);

    // eslint-disable-next-line no-param-reassign
    option.label.fontFamily = overrideIfUndefined(option.label.fontFamily, THS_FONTFAMILY_MEDIUM);

    // * 开启标签展示
    // eslint-disable-next-line no-param-reassign
    option.label.show = overrideIfUndefined(option.label.show, true);
  });
}

export function parseBarColor(seriesOption: BarSeriesOption[]) {
  // echarts默认会在高亮状态给柱子颜色增加明亮度，范式要求去除这种效果
  const defaultEmphasis = {
    disabled: true
  };
  seriesOption.forEach((option, index) => {
    option.emphasis = util.merge(option.emphasis || {}, defaultEmphasis);
    const themeBarColor = getBarColor(index, seriesOption.length === 1);
    if (!isObject(option.itemStyle)) {
      const itemStyle = {
        color: themeBarColor
      };
      option.itemStyle = itemStyle;
    } else {
      option.itemStyle.color = overrideIfUndefined(option.itemStyle.color, themeBarColor);
    }
  });
}
/**
 * 计算柱的圆角
 * 柱宽>=8时，圆角2
 * 柱宽<8，>=4，圆角1
 * 柱宽<4，圆角0
 * @param barWidth 柱子宽度
 */
function calcBorderRadius(barWidth: number) {
  const BAR_WIDTH_8 = 8;
  const BAR_WIDTH_4 = 4;
  const BORDER_RADIUS_2 = 2;
  const BORDER_RADIUS_1 = 1;
  const BORDER_RADIUS_0 = 0;
  let borderRadius: number;
  if (barWidth < BAR_WIDTH_8 && barWidth >= BAR_WIDTH_4) {
    borderRadius = BORDER_RADIUS_1;
  } else if (barWidth < BAR_WIDTH_4) {
    borderRadius = BORDER_RADIUS_0;
  } else {
    borderRadius = BORDER_RADIUS_2;
  }
  return borderRadius;
}

/**
 * 柱状图是否高密度
 * @param barWidth 柱子宽度
 * @return {boolean}
 */
export function isBarHighDensity(barWidth: number) {
  return barWidth < SPECIFICATION.barMaxWidth;
}

function calcBarWidth(seriesOption: BarSeriesOption[], dom: HTMLElement, option: Option) {
  const UNITWIDTHTIMES = 2;
  const gridRect = getGridBoundingRect(dom, option.grid);
  const barNum = seriesOption[0].data.length;
  return Math.min(gridRect.width / barNum / UNITWIDTHTIMES, seriesOption[0].barMaxWidth);
}

/**
 * 柱的动画
 */
function parseBarAnimation(seriesOption: BarSeriesOption[], chart: StandardChart) {
  seriesOption.forEach(option => {
    /* eslint-disable no-param-reassign */
    // ! 堆叠的柱子不处理动画
    // FIXME 会出现堆叠 + 单柱，一个有动画，一个没有动画的情况
    // if (typeof option.stack !== 'undefined') {
    //   return;
    // }

    // 要根据画布高度确定动画效果
    const { height } = chart.getDom().getBoundingClientRect();
    const animationOpts = SPECIFICATION.animation.find(
      item => item.condition.minH < height && height <= item.condition.maxH
    );

    if (animationOpts) {
      option.animationDuration = overrideIfUndefined(option.animationDuration, animationOpts.time);
      // @ts-ignore
      option.animationEasing = overrideIfUndefined(option.animationEasing, animationOpts.easing);
      option.animationDurationUpdate = overrideIfUndefined(
        option.animationDurationUpdate,
        animationOpts.time
      );
      option.animationEasingUpdate = overrideIfUndefined(
        option.animationEasingUpdate,
        // @ts-ignore
        animationOpts.easing
      );
    }

    // * 开启动画
    option.animation = overrideIfUndefined(option.animation, true);
    /* eslint-enable no-param-reassign */
  });
}

function ensureItemStyle(option: BarSeriesOption) {
  if (typeof option.itemStyle === 'undefined') {
    option.itemStyle = {};
  }
}

function ensureData(option: BarSeriesOption) {
  if (typeof option.data === 'undefined') {
    option.data = [];
  }
}

function parseBarBorderRadius(seriesOption: BarSeriesOption[], dom: HTMLElement, option: Option) {
  const barWidth = calcBarWidth(seriesOption, dom, option);
  seriesOption.forEach(el => {
    ensureItemStyle(el);
    if (typeof el.itemStyle.borderRadius === 'undefined') {
      ensureData(el);
      el.data = el.data.map(item => {
        if (util.isString(item) || !util.isObject(item)) {
          // eslint-disable-next-line no-param-reassign
          item = {
            value: item
          };
        }
        // @ts-ignore
        if (typeof item.itemStyle === 'undefined') {
          // @ts-ignore
          // eslint-disable-next-line no-param-reassign
          item.itemStyle = {};
        }
        ensureItemStyle(item);
        const borderRadius = calcBorderRadius(barWidth);
        // @ts-ignore
        // eslint-disable-next-line no-param-reassign
        item.itemStyle.borderRadius = overrideIfUndefined(
          // @ts-ignore
          item.itemStyle.borderRadius,
          // @ts-ignore
          Number(item.value) >= 0
            ? [borderRadius, borderRadius, 0, 0]
            : [0, 0, borderRadius, borderRadius]
        );
        return item;
      });
    }
  });

  // 消除不在最上面的柱子的圆角
  seriesOption
    .filter(bar => bar.stack)
    .forEach((el, index, arr) => {
      el.data.forEach((item, j) => {
        if (item.value !== null && item.value !== undefined) {
          for (let i = 0; i < index; i++) {
            arr[i].data[j].itemStyle.borderRadius = undefined;
          }
        }
      });
    });
}

export function judgeIsNormalBar(option: Option) {
  if (typeof option?.series === 'undefined') {
    return false;
  }
  const barSeries = option.series.filter(item => item.type === 'bar');
  if (barSeries.length === 0) {
    return false;
  }

  // 若是动态条形图则不进行以下柱状图主题解析
  if (judgeIsDynamicBar(barSeries)) {
    return false;
  }
  return true;
}

/**
 * 移动端主题-柱状图解析器
 */
export function barThemeParse(...args: Parameters<ThemeParser>) {
  const [option] = args;
  if (!judgeIsNormalBar(option)) {
    return;
  }
  const barSeries = option.series.filter(item => item.type === 'bar');
  const chart = args[1];
  parseBarSize(barSeries);
  parseBarGap(barSeries);
  parseBarLabel(barSeries);
  parseBarAnimation(barSeries, chart);
  parseBarColor(barSeries);
  parseBarBorderRadius(barSeries, chart.getDom(), option);
  // component按图表需要引入
  GridThemeParse(option, chart);
  xAxisPointerThemeParse(option, chart);
  ToolTipThemeParse(option, chart);

  xAxisThemeParse(...args);
  yAxisThemeParse(...args);
  legendThemeParse(...args);
}
